/* eslint-disable import/extensions,no-underscore-dangle */
/* globals global, __DEV__, PROD_ENV, SENTRY_DSN, GOOGLE_ANALYTICS_ID, FACEBOOK_PIXEL_ID, GOOGLE_OPTIMIZE_ID, ELASTIC_APM_SERVICE_NAME, ELASTIC_APM_SECRET_TOKEN, ELASTIC_APM_SERVER_URL, ELASTIC_APM_ENVIRONMENT */
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import Promise from 'promise'
import ReactGA from 'react-ga4'
import ReactPixel from 'react-facebook-pixel'
import React from 'react'
import * as Sentry from '@sentry/browser'
import { init as initApm } from '@elastic/apm-rum'
import routes from './routes.jsx'
import store, { persistor } from './redux/store'
import { setStore, setRouter } from './utils/navigation'

import './index.css'

// Initialize navigation utilities with the redux store (for transition compatibility)
setStore(store)

// Create the RR6 browser router with future flags for v7 compatibility
const router = createBrowserRouter(routes, {
  future: {
    v7_startTransition: true,
    v7_relativeSplatPath: true
  }
})

// Set the router in navigation utilities for RR6 navigation
setRouter(router)

initApm({
  serviceName: ELASTIC_APM_SERVICE_NAME,
  serverUrl: ELASTIC_APM_SERVER_URL,
  environment: ELASTIC_APM_ENVIRONMENT
})

require('./favicon.ico') // Tell webpack to load favicon.ico

// To add to global
if (!global.Promise) global.Promise = Promise

if (PROD_ENV) {
  // Facebook Pixel initialize
  ReactPixel.init(FACEBOOK_PIXEL_ID)

  // Google Analytics 4 conf
  ReactGA.initialize(GOOGLE_ANALYTICS_ID)
  // Note: Google Optimize was deprecated in Sept 2023
  // ReactGA.ga('require', GOOGLE_OPTIMIZE_ID) - removed as not supported in GA4
}

// Page view tracking - now handled by RR6 router events
// Note: For RR6, page tracking should be handled via useEffect in App component
// or via router.subscribe() if needed

// Setup Sentry
if (!__DEV__)
  Sentry.init({
    dsn: SENTRY_DSN,
    allowUrls: [/stanwinstonschool\.com/, /dev\.swsca\.talpor.com/],
    ignoreErrors: [
      // Random plugins/extensions
      'top.GLOBALS',
      // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
      'originalCreateNotification',
      'canvas.contentDocument',
      'atomicFindClose',
      // Facebook borked
      'fb_xd_fragment',
      // ISP "optimizing" proxy - `Cache-Control: no-transform` seems
      // to reduce this. (thanks @acdha) See
      // http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
      'conduitPage'
    ],
    denyUrls: [/extensions\//i, /^chrome:\/\//i]
  })

// ------------------------------
const root = createRoot(document.getElementById('app'))
root.render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <RouterProvider router={router} future={{ v7_startTransition: true }} />
    </PersistGate>
  </Provider>
)
